import React, {
    Fragment,
    useEffect,
    useState,
    useRef,
    useCallback,
} from 'react'
import { ArrowSmallIcon, Icon } from 'packages/eid-icons'
import {
    Box,
    Radio,
    Skeleton,
    styled,
    Typography,
    useTheme,
} from '@mui/material'
import {
    checkIfAllChildsShowInTree,
    DEFAULT_EMPTY_UUID,
    getFullImageUrl,
    TREE_CHILD_VIEW_LEVELS,
    TREE_VIEW_LEVELS,
} from 'utils'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useTreeFilterAPI } from 'hooks'
import ChildTreeNode from './ChildTreeNode'
import { Tooltip } from 'packages/eid-ui'
import { isNilOrEmpty } from 'packages/core'

const FolderMain = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.4rem',
    '& img': {
        width: '1.5rem',
        marginLeft: '0.5rem',
        cursor: 'pointer',
    },
})

const BusinesRoleAndLocationTreeBox = styled(Box)({
    marginTop: '1rem',
    position: 'relative',
    '& p': {
        fontSize: '1.4rem',
        marginLeft: '0.8rem',
        fontWeight: '600',
        color: '#767676',
    },
})

const FolderInnerItem = styled(Box)({
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    padding: '0rem 0.9rem 0rem 0.8rem',
    borderRadius: '0.5rem',
    '& img': {
        width: '1.5rem',
        marginLeft: '0.5rem',
    },
    '& p': {
        fontSize: '1.4rem',
        marginLeft: '0.8rem',
        cursor: 'pointer',
    },
})

const SelectSection = styled(Box)({
    maxWidth: '30rem',
    '& p': {
        fontWeight: '500',
    },
})

const NodeLabel = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
    '& .search-highlight': {
        color: theme?.palette?.text?.highlightSearchTerm,
        fontWeight: 600,
        textDecoration: 'underline',
    },
    '&.isDisabled': {
        color: '#a3a3aa',
        cursor: 'default',
    },
    '&.selectedNode': {
        background: '#3d3d44',
        padding: '0 0.3rem',
        borderRadius: '0.4rem',
        color: `${theme?.palette?.common?.white} !important`,
    },
}))

const StyledRadio = styled(Radio)({
    padding: '0.2rem',
    maxHeight: '2rem',
    '&:hover': {
        backgroundColor: '#d2d2d9',
    },
    pointerEvents: 'none', // Disable direct interaction
})

const TreeItemWrapper = styled(Box)(({ theme }) => ({
    outline: 'none',
    '&:focus': {
        outline: 'none',
    },
    '&:focus-visible': {
        '& .tree-item-content': {
            outline: `2px solid ${theme?.palette?.primary?.main || '#1976d2'}`,
            outlineOffset: '2px',
            borderRadius: '0.4rem',
        },
    },
}))

const highlight = (text, searchKey) => {
    try {
        searchKey = new RegExp('(' + searchKey + ')', 'gi')
        return text.split(searchKey).map((o, i) =>
            i % 2 === 1 ? (
                <span className="search-highlight" key={i}>
                    {o}
                </span>
            ) : (
                o
            ),
        )
    } catch (err) {
        return text
    }
}

const TreeNode = React.memo(
    ({
        explorer,
        handleSelect,
        value,
        disableAssignableCheck = false,
        highlightText = '',
        titleProp = 'friendlyName',
        valueProp = 'id',
        url,
        showCollapseIcon = true,
        showRadioToSelect = true,
        disableAssignableProp = 'isAssignable',
        onChildrenUpdate,
        ariaLevel = 1,
        expandFirstByDefault = false,
    }) => {
        const { t } = useTranslation()
        const [isOpen, setIsOpen] = useState(
            expandFirstByDefault ||
                (explorer?.rootId === DEFAULT_EMPTY_UUID &&
                    !explorer?.showInTree),
        )
        const [fetchChildren, setFetchChildren] = useState(false)
        const [children, setChildren] = useState(explorer?.children || [])
        const [showTooltip, setShowTooltip] = useState(false)
        const theme = useTheme()
        const nodeRef = useRef(null)

        useEffect(() => {
            setChildren(explorer?.children || [])
        }, [explorer])

        useEffect(() => {
            if (
                highlightText?.length >= 3 &&
                explorer?.hasChildren &&
                !isOpen &&
                explorer?.children?.length
            ) {
                setIsOpen(true)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [highlightText, explorer?.hasChildren])

        const { data, isFetching } = useTreeFilterAPI(
            url.replace(
                `levelsToLoad=${TREE_VIEW_LEVELS}`,
                `levelsToLoad=${TREE_CHILD_VIEW_LEVELS}`,
            ),
            explorer?.id,
            explorer?.id,
            '',
            fetchChildren,
        )

        useEffect(() => {
            if (data && explorer?.id) {
                if (onChildrenUpdate) {
                    onChildrenUpdate(explorer.id, data?.children)
                } else {
                    setChildren(data?.children)
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [data])

        const handleToggle = useCallback(() => {
            setIsOpen((prevIsOpen) => !prevIsOpen)

            // If we're opening (current state is closed), fetch children if needed
            if (!isOpen) {
                if (
                    explorer.hasChildren &&
                    children.length === 0 &&
                    !isFetching
                ) {
                    setFetchChildren((prev) => !prev)
                }
            }
        }, [isOpen, explorer.hasChildren, children.length, isFetching])

        const handleKeyDown = useCallback(
            (event) => {
                const { key } = event

                // Get all tree items in the DOM
                const getAllTreeItems = () => {
                    return Array.from(
                        document.querySelectorAll('[role="treeitem"]'),
                    )
                }

                const currentTreeItems = getAllTreeItems()
                const currentIndex = currentTreeItems.indexOf(nodeRef.current)

                switch (key) {
                    case 'ArrowDown':
                        event.preventDefault()
                        // Move to next tree item
                        if (currentIndex < currentTreeItems.length - 1) {
                            currentTreeItems[currentIndex + 1]?.focus()
                        }
                        break

                    case 'ArrowUp':
                        event.preventDefault()
                        // Move to previous tree item
                        if (currentIndex > 0) {
                            currentTreeItems[currentIndex - 1]?.focus()
                        }
                        break

                    case 'ArrowRight':
                        event.preventDefault()
                        // If has children and is closed, open it
                        if (explorer?.hasChildren && !isOpen) {
                            handleToggle()
                        } else if (
                            explorer?.hasChildren &&
                            isOpen &&
                            children.length > 0
                        ) {
                            // If open and has children, move to first child
                            const firstChild =
                                currentTreeItems[currentIndex + 1]
                            firstChild?.focus()
                        }
                        break

                    case 'ArrowLeft':
                        event.preventDefault()
                        // If has children and is open, close it
                        if (explorer?.hasChildren && isOpen) {
                            handleToggle()
                        } else {
                            // Otherwise move to parent (find previous item with less indentation)
                            for (let i = currentIndex - 1; i >= 0; i--) {
                                const item = currentTreeItems[i]
                                const itemLevel = parseInt(
                                    item.getAttribute('aria-level') || '1',
                                    10,
                                )
                                const currentLevel = parseInt(
                                    nodeRef.current?.getAttribute(
                                        'aria-level',
                                    ) || '1',
                                    10,
                                )
                                if (itemLevel < currentLevel) {
                                    item?.focus()
                                    break
                                }
                            }
                        }
                        break

                    case ' ':
                    case 'Enter':
                        event.preventDefault()
                        // Select the current item if it's selectable
                        if (
                            (explorer?.[disableAssignableProp] === true ||
                                !disableAssignableCheck) &&
                            explorer?.showInTree
                        ) {
                            handleSelect(explorer)
                        }
                        break

                    case 'Home':
                        event.preventDefault()
                        // Move to first tree item
                        currentTreeItems[0]?.focus()
                        break

                    case 'End':
                        event.preventDefault()
                        // Move to last tree item
                        currentTreeItems[currentTreeItems.length - 1]?.focus()
                        break

                    default:
                        break
                }
            },
            [
                explorer,
                isOpen,
                children,
                handleSelect,
                disableAssignableCheck,
                disableAssignableProp,
                handleToggle,
            ],
        )

        return (
            <Fragment>
                {explorer?.showInTree !== false ||
                isNilOrEmpty(explorer) ||
                explorer?.rootId === DEFAULT_EMPTY_UUID ? (
                    <>
                        {explorer?.hasChildren ? (
                            <BusinesRoleAndLocationTreeBox>
                                {!(
                                    explorer?.rootId === DEFAULT_EMPTY_UUID &&
                                    !explorer?.showInTree
                                ) ? (
                                    <TreeItemWrapper
                                        ref={nodeRef}
                                        role="treeitem"
                                        tabIndex={0}
                                        aria-expanded={
                                            explorer?.hasChildren
                                                ? isOpen
                                                : undefined
                                        }
                                        aria-level={ariaLevel}
                                        aria-label={explorer?.[titleProp]}
                                        onKeyDown={handleKeyDown}
                                        onFocus={() => setShowTooltip(true)}
                                        onBlur={() => setShowTooltip(false)}
                                        onMouseEnter={() =>
                                            setShowTooltip(true)
                                        }
                                        onMouseLeave={() =>
                                            setShowTooltip(false)
                                        }
                                    >
                                        <FolderMain className="tree-item-content">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    gap: showRadioToSelect
                                                        ? '1.5rem'
                                                        : '0',
                                                }}
                                            >
                                                {explorer.hasChildren &&
                                                checkIfAllChildsShowInTree(
                                                    explorer,
                                                ) ? (
                                                    isOpen ? (
                                                        showCollapseIcon ? (
                                                            <Box
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation()
                                                                    handleToggle()
                                                                }}
                                                                sx={{
                                                                    '&:before':
                                                                        {
                                                                            content:
                                                                                '""',
                                                                            position:
                                                                                'absolute',
                                                                            width: '.12rem',
                                                                            backgroundColor:
                                                                                'transparent',
                                                                            borderLeft:
                                                                                '1px dotted lightgrey',
                                                                            top: '1.7rem',
                                                                            bottom: '1rem',
                                                                            left: '.7rem',
                                                                            transform:
                                                                                'translateX(-50%)',
                                                                        },
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        height: '1.5rem',
                                                                        '&:before':
                                                                            {
                                                                                content:
                                                                                    '""',
                                                                                position:
                                                                                    'absolute',
                                                                                width: '.12rem',
                                                                                backgroundColor:
                                                                                    'transparent',
                                                                                borderLeft:
                                                                                    '1px dotted lightgrey',
                                                                                top: '-.8rem',
                                                                                bottom: '1rem',
                                                                                left: '.7rem',
                                                                                transform:
                                                                                    'translateX(-50%)',
                                                                                height: '.8rem',
                                                                            },
                                                                        '&:after':
                                                                            {
                                                                                content:
                                                                                    '""',
                                                                                position:
                                                                                    'absolute',
                                                                                width: '.8rem',
                                                                                backgroundColor:
                                                                                    'transparent',
                                                                                borderTop:
                                                                                    '1px dotted lightgrey',
                                                                                top: '1rem',
                                                                                left: '2.2rem',
                                                                                transform:
                                                                                    'translateX(-50%)',
                                                                                height: '.12rem',
                                                                            },
                                                                    }}
                                                                >
                                                                    <Icon name="Collapse" />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <ArrowSmallIcon
                                                                color="#60708f"
                                                                fill="#60708f"
                                                                direction={
                                                                    'down'
                                                                }
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation()
                                                                    handleToggle()
                                                                }}
                                                                aria-label={`${t(
                                                                    'Collapse',
                                                                )} ${
                                                                    explorer?.friendlyName ||
                                                                    explorer?.title
                                                                }`}
                                                            />
                                                        )
                                                    ) : showCollapseIcon ? (
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleToggle()
                                                            }}
                                                            sx={{
                                                                height: '1.5rem',
                                                                '&:before': {
                                                                    content:
                                                                        '""',
                                                                    position:
                                                                        'absolute',
                                                                    width: '.12rem',
                                                                    borderLeft:
                                                                        '1px dotted grey',
                                                                    top: '-.8rem',
                                                                    bottom: '1rem',
                                                                    left: '.7rem',
                                                                    transform:
                                                                        'translateX(-50%)',
                                                                    height: '.8rem',
                                                                },
                                                                '&:after': {
                                                                    content:
                                                                        '""',
                                                                    position:
                                                                        'absolute',
                                                                    width: '.8rem',
                                                                    borderTop:
                                                                        '1px dotted grey',
                                                                    top: '1rem',
                                                                    bottom: '1rem',
                                                                    left: '2.2rem',
                                                                    transform:
                                                                        'translateX(-50%)',
                                                                    height: '.12rem',
                                                                },
                                                            }}
                                                        >
                                                            <Icon name="Expand" />
                                                        </Box>
                                                    ) : (
                                                        <ArrowSmallIcon
                                                            color="#60708f"
                                                            fill="#60708f"
                                                            direction={'right'}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleToggle()
                                                            }}
                                                        />
                                                    )
                                                ) : null}
                                                {(explorer?.imageUrl ||
                                                    explorer?.iconUrl) && (
                                                    <img
                                                        alt="folder-icons"
                                                        src={getFullImageUrl(
                                                            explorer?.imageUrl ||
                                                                explorer?.iconUrl,
                                                        )}
                                                    />
                                                )}
                                                {explorer?.icon && (
                                                    <Icon
                                                        name={explorer.icon}
                                                        width="1.6rem"
                                                        height="1.6rem"
                                                        color={
                                                            theme?.palette
                                                                ?.primary?.main
                                                        }
                                                    />
                                                )}
                                                {showRadioToSelect && (
                                                    <Box
                                                        onClick={() => {
                                                            if (
                                                                (explorer?.[
                                                                    disableAssignableProp
                                                                ] === true ||
                                                                    !disableAssignableCheck) &&
                                                                explorer?.showInTree
                                                            ) {
                                                                handleSelect(
                                                                    explorer,
                                                                )
                                                            }
                                                        }}
                                                        sx={{
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <StyledRadio
                                                            checkedIcon={
                                                                <Icon
                                                                    name="RadioFilled"
                                                                    color={
                                                                        theme
                                                                            ?.palette
                                                                            ?.primary
                                                                            ?.main
                                                                    }
                                                                />
                                                            }
                                                            icon={
                                                                <Icon name="Radio" />
                                                            }
                                                            checked={
                                                                value ===
                                                                    explorer?.[
                                                                        valueProp
                                                                    ] ?? false
                                                            }
                                                            disabled={
                                                                (explorer?.[
                                                                    disableAssignableProp
                                                                ] === false &&
                                                                    disableAssignableCheck) ||
                                                                !explorer?.showInTree
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                            <Tooltip
                                                open={showTooltip}
                                                title={
                                                    explorer?.alreadyAssigned
                                                        ? t('AlreadyAssigned')
                                                        : explorer?.isAssignable ===
                                                              false &&
                                                          disableAssignableCheck
                                                        ? t(
                                                              'NotAvailableForAssignment',
                                                          )
                                                        : !explorer?.showInTree
                                                        ? t('NotForSelection')
                                                        : ''
                                                }
                                                disableHoverListener
                                                disableFocusListener
                                            >
                                                <NodeLabel
                                                    className={classNames({
                                                        selectedNode:
                                                            explorer?.[
                                                                valueProp
                                                            ] &&
                                                            value ===
                                                                explorer?.[
                                                                    valueProp
                                                                ],
                                                        isDisabled:
                                                            (explorer?.[
                                                                disableAssignableProp
                                                            ] === false &&
                                                                disableAssignableCheck) ||
                                                            !explorer?.showInTree,
                                                    })}
                                                    onClick={() => {
                                                        if (
                                                            (explorer?.[
                                                                disableAssignableProp
                                                            ] === true ||
                                                                !disableAssignableCheck) &&
                                                            explorer?.showInTree
                                                        ) {
                                                            handleSelect(
                                                                explorer,
                                                            )
                                                        }
                                                    }}
                                                >
                                                    {highlightText &&
                                                    highlightText.length
                                                        ? highlight(
                                                              explorer?.[
                                                                  titleProp
                                                              ],
                                                              highlightText,
                                                          )
                                                        : explorer?.[titleProp]}
                                                </NodeLabel>
                                            </Tooltip>
                                        </FolderMain>
                                    </TreeItemWrapper>
                                ) : null}

                                {isOpen && isFetching ? (
                                    <Box sx={{ paddingLeft: '1.5rem' }}>
                                        <Skeleton height={20} />
                                        <Skeleton height={20} />
                                    </Box>
                                ) : null}

                                {isOpen && children.length > 0 && (
                                    <Fragment>
                                        {children.map((item, index) => (
                                            <div
                                                style={{
                                                    paddingLeft: !(
                                                        explorer?.rootId ===
                                                            DEFAULT_EMPTY_UUID &&
                                                        !explorer?.showInTree
                                                    )
                                                        ? '2.2rem'
                                                        : '',
                                                }}
                                                key={`${item?.id}-${index}`}
                                            >
                                                <ChildTreeNode
                                                    item={item}
                                                    handleSelect={handleSelect}
                                                    value={value}
                                                    disableAssignableCheck={
                                                        disableAssignableCheck
                                                    }
                                                    highlightText={
                                                        highlightText
                                                    }
                                                    url={url.replace(
                                                        `levelsToLoad=${TREE_VIEW_LEVELS}`,
                                                        `levelsToLoad=${TREE_CHILD_VIEW_LEVELS}`,
                                                    )}
                                                    titleProp={titleProp}
                                                    valueProp={valueProp}
                                                    showRadioToSelect={
                                                        showRadioToSelect
                                                    }
                                                    showCollapseIcon={
                                                        showCollapseIcon
                                                    }
                                                    disableAssignableProp={
                                                        disableAssignableProp
                                                    }
                                                    onChildrenUpdate={
                                                        onChildrenUpdate
                                                    }
                                                    ariaLevel={ariaLevel + 1}
                                                />
                                            </div>
                                        ))}
                                    </Fragment>
                                )}
                            </BusinesRoleAndLocationTreeBox>
                        ) : explorer?.[valueProp] && explorer?.[titleProp] ? (
                            <TreeItemWrapper
                                ref={nodeRef}
                                role="treeitem"
                                tabIndex={0}
                                aria-level={ariaLevel}
                                aria-label={explorer?.[titleProp]}
                                onKeyDown={handleKeyDown}
                                onFocus={() => setShowTooltip(true)}
                                onBlur={() => setShowTooltip(false)}
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <FolderInnerItem
                                    className={`${SelectSection} tree-item-content`}
                                >
                                    {(explorer?.imageUrl ||
                                        explorer?.iconUrl) && (
                                        <img
                                            alt="folder-icons"
                                            src={getFullImageUrl(
                                                explorer?.imageUrl ||
                                                    explorer?.iconUrl,
                                            )}
                                        />
                                    )}
                                    {showRadioToSelect && (
                                        <Box
                                            sx={{
                                                paddingLeft: '2.2rem',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                if (
                                                    (explorer?.[
                                                        disableAssignableProp
                                                    ] === true ||
                                                        !disableAssignableCheck) &&
                                                    explorer?.showInTree
                                                ) {
                                                    handleSelect(explorer)
                                                }
                                            }}
                                        >
                                            <StyledRadio
                                                checkedIcon={
                                                    <Icon
                                                        name="RadioFilled"
                                                        color={
                                                            theme?.palette
                                                                ?.primary?.main
                                                        }
                                                    />
                                                }
                                                icon={<Icon name="Radio" />}
                                                checked={
                                                    value ===
                                                        explorer?.[valueProp] ??
                                                    false
                                                }
                                                disabled={
                                                    (explorer?.[
                                                        disableAssignableProp
                                                    ] === false &&
                                                        disableAssignableCheck) ||
                                                    !explorer?.showInTree
                                                }
                                            />
                                        </Box>
                                    )}
                                    <Tooltip
                                        open={showTooltip}
                                        title={
                                            explorer?.alreadyAssigned
                                                ? t('AlreadyAssigned')
                                                : explorer?.isAssignable ===
                                                      false &&
                                                  disableAssignableCheck
                                                ? t('NotAvailableForAssignment')
                                                : !explorer?.showInTree
                                                ? t('NotForSelection')
                                                : ''
                                        }
                                        disableHoverListener
                                        disableFocusListener
                                    >
                                        <NodeLabel
                                            className={classNames({
                                                selectedNode:
                                                    explorer?.[valueProp] &&
                                                    value ===
                                                        explorer?.[valueProp],
                                                isDisabled:
                                                    (explorer?.[
                                                        disableAssignableProp
                                                    ] === false &&
                                                        disableAssignableCheck) ||
                                                    !explorer?.showInTree,
                                            })}
                                            onClick={() => {
                                                if (
                                                    (explorer?.[
                                                        disableAssignableProp
                                                    ] === true ||
                                                        !disableAssignableCheck) &&
                                                    explorer?.showInTree
                                                ) {
                                                    handleSelect(explorer)
                                                }
                                            }}
                                        >
                                            {highlightText &&
                                            highlightText.length
                                                ? highlight(
                                                      explorer?.[titleProp],
                                                      highlightText,
                                                  )
                                                : explorer?.[titleProp]}
                                        </NodeLabel>
                                    </Tooltip>
                                </FolderInnerItem>
                            </TreeItemWrapper>
                        ) : (
                            <Box>
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    {t('Common_NoDataFound')}
                                </Typography>
                            </Box>
                        )}
                    </>
                ) : null}
            </Fragment>
        )
    },
)

export default TreeNode
